import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, ReactNode } from 'react'
import {
  getPreferenceStorageKey,
  SYSTEM_DEFAULTS,
  type UserPreferences,
  type Theme,
} from '@airclou/utils'

interface UserPreferencesContextType {
  /**
   * The slug this provider was mounted with. Exposed so consumers that need it
   * — `LanguageSync`, `resolveDisplayLanguage` — read it from the one place it
   * was declared instead of repeating the literal. It was written three times
   * per app; a single disagreement would silently split a user's preferences
   * across two localStorage namespaces.
   */
  appSlug: string
  /** Resolved preferences (after applying resolution order) */
  preferences: UserPreferences
  /** The resolved actual theme ('light' | 'dark'), accounting for 'system' */
  actualTheme: 'light' | 'dark'
  /** Update a single preference (writes to localStorage + calls onSave) */
  setPreference: <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => void
  /** Whether a save is in progress */
  saving: boolean
  /**
   * The preference whose last save failed, or null. The value has already
   * been put back; this is what lets the page say so. Cleared the next time
   * that preference is set.
   */
  lastSaveError: keyof UserPreferences | null
  /**
   * Has anyone actually chosen this preference, as opposed to it resolving to
   * the system default? True when this browser holds a value for it, when the
   * account does, or when this session just changed it. `LanguageSync` asks,
   * because a value that fell back to the default when the account layer was
   * cleared must not be mistaken for someone choosing the default.
   */
  isChosen: (key: keyof UserPreferences) => boolean
  /**
   * Feed in the account-wide layer once it can be fetched.
   *
   * `GlobalPreferencesSync` calls this; it is separate from the `globalDefaults`
   * prop because most apps mount this provider above their auth provider (so
   * the public pages are themed too), and the account-wide values cannot be
   * read until a session exists further down the tree. `null` means the
   * session that answered is over.
   */
  applyGlobalDefaults: (defaults: Partial<UserPreferences> | null) => void
  /**
   * Register the writer for the account-wide layer, so a choice made in any app
   * becomes the default in the others. Pass `null` to unregister.
   */
  setGlobalSaver: (saver: GlobalPreferenceSaver | null) => void
}

/** Persists one preference to the account-wide store (Auth). */
export type GlobalPreferenceSaver = (
  key: keyof UserPreferences,
  value: string
) => Promise<void>

const UserPreferencesContext = createContext<UserPreferencesContextType | undefined>(undefined)

export interface UserPreferencesProviderProps {
  children: ReactNode
  /** App slug for localStorage key namespacing (e.g. 'playbook', 'helpdesk') */
  appSlug: string
  /**
   * Account-wide defaults from Auth (`user_settings`), for an app that fetches
   * them itself. Most apps leave this alone and mount `GlobalPreferencesSync`
   * instead, which feeds the same layer from below the auth provider.
   */
  globalDefaults?: Partial<UserPreferences> | null
  /**
   * A preference this app stores for itself, overriding the account-wide value.
   *
   * No app passes this today. Every app that did was writing the row from its
   * generic Preferences tab rather than anyone choosing an override, so the
   * only effect was that the app stopped following a preference set elsewhere.
   * The layer stays because a genuine per-app override is a coherent thing to
   * want; nothing currently is one.
   */
  appOverrides?: Partial<UserPreferences> | null
  /** Callback to persist a preference change to the server */
  onSave?: (key: keyof UserPreferences, value: string) => Promise<void>
}

const ALL_KEYS: readonly (keyof UserPreferences)[] = ['theme', 'language', 'timezone']

/**
 * Which cached values survive the end of the session that answered for them.
 *
 * All three preferences belong to the person and are held by the account; what
 * this browser keeps for each is a cache, written when the account answers and
 * removed when it stops answering. The only question this list settles is what
 * happens when a *different* person signs in on this browser.
 *
 * Language and timezone go, because they disclose something: a timezone says
 * roughly where someone is and when they work, and the next person must not
 * read the last person's. Theme stays, because it discloses nothing, and
 * clearing it empties the key an app's pre-paint script reads — so a dark-mode
 * user would get a white flash on the first load after every sign-in. The cost
 * of keeping it is that the new person may see the previous person's theme for
 * a single paint, before the account answers.
 *
 * Only dashboard and playbook have such a script today (helpdesk, conductor
 * and form-builder flash on every load, pre-paint or not — see the doc). That
 * does not weaken the decision, which costs nothing and discloses nothing; it
 * means the benefit is not yet suite-wide.
 *
 * Theme used to sit in a device-local list instead, meaning the account could
 * never overwrite it. Combined with seeding a browser that held nothing, that
 * produced a rule nobody would choose: theme followed the account exactly once
 * per app and never again. See the "Theme is account-wide, like the other two"
 * section of docs/architecture/user-preferences-architecture.md.
 */
export const IDENTITY_PERSISTENT_KEYS: readonly (keyof UserPreferences)[] = ['theme']

/**
 * Where the provider records which cached keys are copies of the account.
 *
 * The distinction matters for one reason: a value this browser held *before*
 * the account layer existed is the only copy of that choice for apps that
 * never had a server row of their own. Clearing or pruning it would lose it
 * for good. So only keys the account has answered for are ever touched by the
 * account; a legacy value stays exactly as it always was — per browser — until
 * the person re-picks and it becomes an account value too.
 */
function accountKeysStorageKey(appSlug: string): string {
  return getPreferenceStorageKey(appSlug, 'accountPreferenceKeys')
}

function readAccountKeys(appSlug: string): Set<keyof UserPreferences> {
  try {
    const raw = localStorage.getItem(accountKeysStorageKey(appSlug))
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return new Set(
      Array.isArray(parsed)
        ? parsed.filter((k): k is keyof UserPreferences => ALL_KEYS.includes(k as keyof UserPreferences))
        : [],
    )
  } catch {
    return new Set()
  }
}

function writeAccountKeys(appSlug: string, keys: Set<keyof UserPreferences>): void {
  try {
    if (keys.size === 0) localStorage.removeItem(accountKeysStorageKey(appSlug))
    else localStorage.setItem(accountKeysStorageKey(appSlug), JSON.stringify([...keys]))
  } catch {
    // localStorage unavailable
  }
}

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return 'light'
}

function readLocalStoragePreferences(appSlug: string): Partial<UserPreferences> {
  const result: Partial<UserPreferences> = {}
  try {
    const theme = localStorage.getItem(getPreferenceStorageKey(appSlug, 'theme'))
    if (theme) result.theme = theme as Theme
    const language = localStorage.getItem(getPreferenceStorageKey(appSlug, 'language'))
    if (language) result.language = language
    const timezone = localStorage.getItem(getPreferenceStorageKey(appSlug, 'timezone'))
    if (timezone) result.timezone = timezone
  } catch {
    // localStorage unavailable
  }
  return result
}

export function UserPreferencesProvider({
  children,
  appSlug,
  globalDefaults,
  appOverrides,
  onSave,
}: UserPreferencesProviderProps) {
  const [localOverrides, setLocalOverrides] = useState<Partial<UserPreferences>>(() =>
    readLocalStoragePreferences(appSlug)
  )
  const [saving, setSaving] = useState(false)
  const [lastSaveError, setLastSaveError] = useState<keyof UserPreferences | null>(null)
  // The account-wide layer, when something below the provider fetched it. The
  // prop wins when an app passes its own, which keeps this an addition rather
  // than a second source of truth.
  const [fetchedGlobalDefaults, setFetchedGlobalDefaults] =
    useState<Partial<UserPreferences> | null>(null)
  const globalSaverRef = useRef<GlobalPreferenceSaver | null>(null)
  // Read inside stable callbacks: making them depend on `appSlug` would change
  // their identity, and the sync component holds them in an effect dependency
  // list.
  const appSlugRef = useRef(appSlug)
  appSlugRef.current = appSlug
  const localOverridesRef = useRef(localOverrides)
  localOverridesRef.current = localOverrides
  /**
   * The value each key held before the most recent `setPreference` wrote it,
   * kept here rather than read off the render-refreshed ref: two writes to one
   * key inside a single tick see the same stale ref and both capture the
   * value from before the first, so a failure of the second would roll back
   * past a save the account had already accepted.
   */
  const lastWritten = useRef<Partial<UserPreferences>>({})
  const appOverridesRef = useRef(appOverrides)
  appOverridesRef.current = appOverrides
  /**
   * Preferences this session has changed, so a slower read of the account-wide
   * store cannot answer for them with the value they used to have.
   */
  const changedThisSession = useRef(new Set<keyof UserPreferences>())
  /** Cached keys that are copies of the account — the only ones the account may touch. */
  const accountKeys = useRef<Set<keyof UserPreferences> | null>(null)
  // Read once, not on every render — `useRef(expr)` evaluates `expr` each time.
  if (accountKeys.current === null) accountKeys.current = readAccountKeys(appSlug)
  const accountKeysRef = () => accountKeys.current as Set<keyof UserPreferences>
  /**
   * Bumped whenever the account layer is cleared. A save's rollback compares
   * against it: if the session that made the save has ended, there is nothing
   * to put back — restoring would re-plant the previous person's value as a
   * legacy one the account can never prune.
   */
  const sessionGeneration = useRef(0)
  const effectiveGlobalDefaults = fetchedGlobalDefaults

  const applyGlobalDefaults = useCallback((defaults: Partial<UserPreferences> | null) => {
    const slug = appSlugRef.current

    // The session that answered is over. Its answers go — the layer, and the
    // cached copies of them — or the next person to sign in on this browser
    // reads in the last person's language until their own arrives, or forever
    // if they have chosen none. Only the copies: a legacy value the account
    // never answered for is left alone, see `accountKeysStorageKey`.
    if (!defaults) {
      sessionGeneration.current += 1
      changedThisSession.current.clear()
      setFetchedGlobalDefaults(null)
      const toClear = [...accountKeysRef()].filter((key) => !IDENTITY_PERSISTENT_KEYS.includes(key))
      // A kept value keeps its marking. The marking is what makes it prunable
      // — the refresh path only removes a key the account "once answered for"
      // — so dropping it here would leave the previous person's theme in place
      // for a next person who has chosen none, forever, rather than for the
      // one paint before their own answer lands.
      accountKeys.current = new Set(
        [...accountKeysRef()].filter((key) => IDENTITY_PERSISTENT_KEYS.includes(key)),
      )
      writeAccountKeys(slug, accountKeysRef())
      if (toClear.length === 0) return
      // Side effects outside the updater, same as the refresh path below: an
      // updater must be pure.
      for (const key of toClear) {
        try {
          localStorage.removeItem(getPreferenceStorageKey(slug, key))
        } catch {
          // localStorage unavailable
        }
      }
      setLocalOverrides((prev) => {
        const next = { ...prev }
        for (const key of toClear) delete next[key]
        return next
      })
      return
    }

    // The layer is stored as the account answered it. What this browser holds
    // outranks it, so a value this session just chose is never displaced by
    // it — and keeping the account's answer intact means a rollback that
    // removes the local value still has something true to fall back to.
    setFetchedGlobalDefaults(defaults)

    // Keep the cache in step with the account, for the keys the cache only
    // caches. Written when the account answers, so the next load paints the
    // current value before the fetch; removed when the account has stopped
    // answering for a key it once did, so a preference cleared elsewhere does
    // not live on here. Theme is left alone: there, the local value is a
    // choice, not a copy.
    //
    // Decided here, outside the state updater, and applied to the refs and to
    // localStorage exactly once. An updater must be pure: React may run it
    // twice (StrictMode does, on every dev render), and a second pass that
    // finds the ref already changed would skip the branch and hand back the
    // stale state.
    const writes: [keyof UserPreferences, string][] = []
    const removals: (keyof UserPreferences)[] = []
    for (const key of ALL_KEYS) {
      // A key this session already changed is not open to being answered by
      // a read that started before the change. The read is simply older: the
      // save is on its way to the same store the read came from, and the read
      // cannot know about it. Without this, choosing a timezone in the second
      // after a page loads gets silently undone when the fetch lands.
      if (changedThisSession.current.has(key)) continue
      // A key the app overrides is answered by the override, so there is
      // nothing of the account's to cache for it — and a cached copy would
      // sit above the override in the order and defeat it.
      if (appOverridesRef.current?.[key]) continue
      const value = defaults[key]
      if (value) {
        accountKeysRef().add(key)
        writes.push([key, value])
      } else if (accountKeysRef().has(key)) {
        accountKeysRef().delete(key)
        removals.push(key)
      }
    }
    writeAccountKeys(slug, accountKeysRef())
    for (const [key, value] of writes) {
      try {
        localStorage.setItem(getPreferenceStorageKey(slug, key), value)
      } catch {
        // localStorage unavailable
      }
    }
    for (const key of removals) {
      try {
        localStorage.removeItem(getPreferenceStorageKey(slug, key))
      } catch {
        // localStorage unavailable
      }
    }
    if (writes.length === 0 && removals.length === 0) return
    setLocalOverrides((prev) => {
      const next = { ...prev }
      for (const [key, value] of writes) next[key] = value as any
      for (const key of removals) delete next[key]
      return next
    })
  }, [])

  // An app that fetches the account layer itself passes it as a prop; it goes
  // through the same path the sync uses, so the cache, the marker and the
  // first-paint theme are kept in step either way. (No app passes it today.)
  useEffect(() => {
    if (globalDefaults !== undefined) applyGlobalDefaults(globalDefaults)
  }, [globalDefaults, applyGlobalDefaults])

  const setGlobalSaver = useCallback((saver: GlobalPreferenceSaver | null) => {
    globalSaverRef.current = saver
  }, [])

  // One order for every key: what this browser holds, then an app's own
  // override, then the account, then the system default. The cache for
  // language and timezone earns the first slot because `applyGlobalDefaults`
  // keeps it in step with the account; a stale copy cannot win for long.
  const preferences = useMemo<UserPreferences>(() => {
    const merged: Partial<UserPreferences> = {}
    for (const key of ALL_KEYS) {
      merged[key] = (localOverrides[key] ||
        appOverrides?.[key] ||
        effectiveGlobalDefaults?.[key] ||
        SYSTEM_DEFAULTS[key]) as any
    }
    return merged as UserPreferences
  }, [localOverrides, appOverrides, effectiveGlobalDefaults])

  const isChosen = useCallback(
    (key: keyof UserPreferences) =>
      Boolean(localOverrides[key]) ||
      Boolean(effectiveGlobalDefaults?.[key]) ||
      changedThisSession.current.has(key),
    [localOverrides, effectiveGlobalDefaults],
  )

  const actualTheme = useMemo<'light' | 'dark'>(
    () => (preferences.theme === 'system' ? getSystemTheme() : preferences.theme),
    [preferences.theme]
  )

  // Apply theme to document
  useEffect(() => {
    const html = document.documentElement
    if (actualTheme === 'dark') {
      html.classList.add('dark')
      html.classList.remove('light')
    } else {
      html.classList.remove('dark')
      html.classList.add('light')
    }
  }, [actualTheme])

  // Listen for system theme changes when theme is 'system'
  useEffect(() => {
    if (preferences.theme !== 'system') return

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => {
      // Force re-resolve by nudging localOverrides (system theme changed)
      setLocalOverrides((prev) => ({ ...prev }))
    }
    mediaQuery.addEventListener('change', handler)
    return () => mediaQuery.removeEventListener('change', handler)
  }, [preferences.theme])

  const setPreference = useCallback(
    <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => {
      const previousLocal = key in lastWritten.current ? lastWritten.current[key] : localOverridesRef.current[key]
      lastWritten.current[key] = value
      const generation = sessionGeneration.current

      // 1. Write to localStorage immediately for instant UI response
      try {
        localStorage.setItem(getPreferenceStorageKey(appSlug, key), value as string)
      } catch {
        // localStorage unavailable
      }

      // 2. Update local state, and remember that this session owns this key now
      setLocalOverrides((prev) => ({ ...prev, [key]: value }))
      changedThisSession.current.add(key)
      setLastSaveError((prev) => (prev === key ? null : prev))

      // 3. Persist in the background — to this app's own store when it has
      //    one, and to the account-wide store so the other apps inherit the
      //    choice. Both get the same value, so they cannot disagree because of
      //    this write.
      const writes: Promise<unknown>[] = []
      if (onSave) writes.push(onSave(key, value as string))
      const accountWrite = globalSaverRef.current?.(key, value as string)
      if (accountWrite) writes.push(accountWrite)
      if (writes.length === 0) return

      setSaving(true)
      // allSettled, not all: one store being down must not leave `saving`
      // stuck on, nor stop the other write from being reported. A rejection
      // still has to say so — allSettled discards it otherwise, and a
      // preference that silently did not persist looks exactly like one that
      // did until the next page load.
      void Promise.allSettled(writes).then((results) => {
        setSaving(false)
        const failed = results.some((result) => result.status === 'rejected')
        for (const result of results) {
          if (result.status === 'rejected') {
            console.error(`Failed to save preference "${String(key)}":`, result.reason)
          }
        }
        if (!failed) {
          if (!accountWrite) return
          // The session that made this save has ended: its answers were swept,
          // but this value joined the cache before it could be marked as the
          // account's, so the sweep left it — for whoever signs in next. Take
          // it out now; the account has it, and the next read will bring it
          // back for the right person. A key the sweep deliberately keeps
          // (theme) is left alone here too: removing it would undo that
          // decision through the back door and bring the first-paint flash
          // back with it.
          if (generation !== sessionGeneration.current && IDENTITY_PERSISTENT_KEYS.includes(key)) return
          if (generation !== sessionGeneration.current) {
            setLocalOverrides((prev) => {
              if (prev[key] !== value) return prev
              const next = { ...prev }
              delete next[key]
              return next
            })
            // Only if storage still holds this value: the next session may
            // already have written its own, and that one stays.
            try {
              const storageKey = getPreferenceStorageKey(appSlug, key)
              if (localStorage.getItem(storageKey) === value) localStorage.removeItem(storageKey)
            } catch {
              // localStorage unavailable
            }
            return
          }
          // The account holds this value now, so the cached copy is a copy —
          // it may be cleared with the session and pruned if the account ever
          // stops answering for it.
          accountKeysRef().add(key)
          writeAccountKeys(appSlug, accountKeysRef())
          return
        }
        // A newer save of the same key may have landed meanwhile; if the value
        // on screen is no longer the one this call wrote, this failure is
        // history — nothing to take back, nothing to report.
        if (localOverridesRef.current[key] !== value) return
        // The key comes off the changed list, so the next read of the account
        // may answer for it again — and the page is told, because a control
        // that snapped back with no word is a control the person will keep
        // clicking.
        changedThisSession.current.delete(key)
        setLastSaveError(key)
        // A save whose session has since ended has nothing to put back: the
        // layer was cleared, and restoring would re-plant the previous
        // person's value as one the account can never prune.
        if (generation !== sessionGeneration.current) return
        // Otherwise put things back the way they were. Keeping the optimistic
        // value would show a choice as applied that exists in this browser
        // only — the "cached copy shadows the account" failure this design
        // removes.
        setLocalOverrides((prev) => {
          if (prev[key] !== value) return prev
          const next = { ...prev }
          if (previousLocal === undefined) delete next[key]
          else next[key] = previousLocal
          return next
        })
        try {
          if (previousLocal === undefined) localStorage.removeItem(getPreferenceStorageKey(appSlug, key))
          else localStorage.setItem(getPreferenceStorageKey(appSlug, key), previousLocal as string)
        } catch {
          // localStorage unavailable
        }
      })
    },
    [appSlug, onSave]
  )

  const value = useMemo(
    () => ({
      appSlug,
      preferences,
      actualTheme,
      setPreference,
      saving,
      lastSaveError,
      isChosen,
      applyGlobalDefaults,
      setGlobalSaver,
    }),
    [appSlug, preferences, actualTheme, setPreference, saving, lastSaveError, isChosen, applyGlobalDefaults, setGlobalSaver]
  )

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  )
}

/**
 * The context if a provider is above, otherwise undefined. For display-only
 * hooks with a sane default (`useTimezone`), which may legitimately render
 * where no provider is mounted — an isolated widget, a component test — and
 * should not make that a crash.
 */
export function useOptionalUserPreferences(): UserPreferencesContextType | undefined {
  return useContext(UserPreferencesContext)
}

export function useUserPreferences(): UserPreferencesContextType {
  const context = useContext(UserPreferencesContext)
  if (!context) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider')
  }
  return context
}
